import { useState, FormEvent } from 'react'
import type { Mismatch, ResolutionType } from '../types'
import { money } from '../lib/format'

const OPTIONS: { value: ResolutionType; label: string }[] = [
  { value: 'credit_given',      label: 'Credit given (missing package credited)' },
  { value: 'refunded',          label: 'Refunded (double charge / customer refund)' },
  { value: 'manual_adjustment', label: 'Manual adjustment in ledger' },
  { value: 'cancelled_ok',      label: 'Legitimate cancel — no action needed' },
  { value: 'escalated_to_psp',  label: 'Escalated to PSP (awaiting resolution)' },
  { value: 'other',             label: 'Other (see notes)' },
]

interface Props {
  mismatch: Mismatch
  onClose: () => void
  onSubmit: (type: ResolutionType, notes: string) => Promise<void>
}

export function ResolveModal({ mismatch, onClose, onSubmit }: Props) {
  const [type, setType] = useState<ResolutionType>('credit_given')
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (!notes.trim()) {
      setError('Notes are required — at minimum a ticket id or one-liner.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await onSubmit(type, notes.trim())
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-6 z-50"
      onClick={onClose}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-lg bg-slate-900 border border-slate-800 rounded-xl p-6 space-y-4"
      >
        <div>
          <h2 className="text-lg font-semibold">Resolve mismatch</h2>
          <p className="text-slate-400 text-sm mt-1">
            Reference{' '}
            <span className="font-mono text-slate-200">{mismatch.reference}</span>
            {' · '}
            {mismatch.mismatch_type}
            {' · '}
            PSP {money(mismatch.paycom_amount)}
            {' / '}
            Omega {money(mismatch.omega_amount)}
          </p>
        </div>

        <label className="block text-sm">
          Resolution type
          <select
            value={type}
            onChange={(e) => setType(e.target.value as ResolutionType)}
            className="mt-1 w-full rounded bg-slate-800 border border-slate-700 px-3 py-2"
          >
            {OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>

        <label className="block text-sm">
          Notes (required)
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
            placeholder="Ticket id, what you did, links…"
            className="mt-1 w-full rounded bg-slate-800 border border-slate-700 px-3 py-2"
          />
        </label>

        {error && <div className="text-red-400 text-sm">{error}</div>}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 rounded text-sm text-slate-300 hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            className="px-3 py-2 rounded text-sm bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50"
          >
            {busy ? 'Saving…' : 'Resolve'}
          </button>
        </div>
      </form>
    </div>
  )
}
